import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, FileText, Loader2 } from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';

export default function SurveysPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuthStore();

  const { data: surveys, isLoading, error } = useQuery({
    queryKey: ['survey-templates', user?.organization_id],
    queryFn: () => user?.organization_id ? api.getSurveyTemplates(user.organization_id) : Promise.resolve([]),
    enabled: !!user?.organization_id,
  });

  const filteredSurveys = (surveys || []).filter((survey) =>
    survey.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        Failed to load survey templates. Please try again later.
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Survey Templates</h1>
          <p className="text-muted-foreground">Create and manage your survey templates</p>
        </div>
        <Link to="/surveys/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            New Survey
          </Button>
        </Link>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search surveys..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredSurveys.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No survey templates yet</h3>
          <p className="text-muted-foreground mb-4">Create your first survey template to get started</p>
          <Link to="/surveys/new">
            <Button variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Create Survey
            </Button>
          </Link>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredSurveys.map((survey) => (
            <Link
              key={survey.id}
              to={`/surveys/${survey.id}`}
              className="p-6 bg-card rounded-lg border border-border hover:border-primary transition-colors group"
            >
              <div className="flex items-start justify-between">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <span className="text-xs text-muted-foreground">v{survey.version}</span>
              </div>
              <h3 className="font-semibold mt-4 group-hover:text-primary transition-colors">{survey.name}</h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {survey.description || 'No description'}
              </p>
              <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                <span>Updated {new Date(survey.updated_at).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
